import { addDays, endOfMonth, format, formatISO, isValid, parseISO, setDate, subMonths } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/** テナントのタイムゾーン。API の Organization.timezone と同じく、現状は全テナント JST 固定 */
export const TENANT_TIME_ZONE = 'Asia/Tokyo';

export const formatYMDjp = (value?: string) => {
  if (!value) {
    return '-';
  }
  const parsedDate = parseISO(value);
  return format(parsedDate, 'yyyy年M月d日');
};

export const formatYMDjpHm = (value?: string) => {
  if (!value) {
    return '-';
  }
  const parsedDate = parseISO(value);
  return format(parsedDate, 'yyyy年M月d日 HH:mm');
};

/**
 * ISO文字列をJST固定の「yyyy年M月d日 HH:mm」で表示する
 * 納品書メールの送信予約のように、JST基準で決まる日時を端末TZに依存させたくない場合に使う
 */
export const formatJstYMDjpHm = (value?: string): string => {
  if (!value) {
    return '-';
  }
  const parsedDate = parseISO(value);
  if (!isValid(parsedDate)) {
    return '-';
  }
  return formatInTimeZone(parsedDate, TENANT_TIME_ZONE, 'yyyy年M月d日 HH:mm');
};

/*
 * dateFormat に合わない入力は react-datepicker が new Date() にフォールバックするため、
 * "20261/10/01" のような打ちかけの値でも 20261 年の Date が onChange に届く。
 * 5 桁年は yyyy-MM-dd に収まらず、次の描画の parseISO が Invalid Date になって
 * DatePicker が RangeError で落ちるので、4 桁年に収まる日付だけを扱う。
 */
export const isSupportedDate = (date: Date) =>
  isValid(date) && date.getFullYear() >= 1000 && date.getFullYear() <= 9999;

/** DatePicker の selected などに渡すための yyyy-MM-dd のパース。空や扱えない値は null */
export const parseSupportedDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = parseISO(value);
  return isSupportedDate(date) ? date : null;
};

export const formatYMD = (value?: string, emptyValue = '-', formatStr = 'yyyy-MM-dd') => {
  if (!value) {
    return emptyValue;
  }
  const parsedDate = parseISO(value);
  return format(parsedDate, formatStr);
};

export const formatYMDhms = (value?: string, emptyValue = '-', formatStr = 'yyyy-MM-dd HH:mm:ss') =>
  formatYMD(value, emptyValue, formatStr);

/**
 * APIのDateTimeField(UTCのISO文字列)をテナントのTZの暦日(YYYY-MM-DD)に正規化する
 * バックエンドが帳票データを組み立てる際もテナントの暦日に変換するため、
 * 帳票へ日付を渡すときは端末のタイムゾーンに依存しないこの関数を使う
 */
export const toTenantDateString = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const parsedDate = parseISO(value);
  if (!isValid(parsedDate)) return undefined;
  return formatInTimeZone(parsedDate, TENANT_TIME_ZONE, 'yyyy-MM-dd');
};

/** 今日のテナントの暦日(YYYY-MM-DD)。API が出荷日などの既定値に使う暦日と揃える */
export const todayTenantDateString = (): string =>
  formatInTimeZone(new Date(), TENANT_TIME_ZONE, 'yyyy-MM-dd');

/** 今日のテナントの暦日。ロットの有効期限など日付だけの値(parseISO)と暦日で比べるときに使う */
export const todayTenantDate = (): Date => parseISO(todayTenantDateString());

/**
 * 日付だけで入力した在庫の変動日(生産完了日など)を、API に送る日時(ISO)に変換する
 * 今日は実行時刻、それ以外はテナントのTZでその日の終わりとする(棚卸の完了日と同じ規則)。
 * 日付の0時にすると、同じ日にそれより後で入庫したロットをその時点の在庫として使えない
 */
export const tenantDateToStockChangedAt = (dateString: string): string => {
  if (!isValid(parseISO(dateString))) return '';
  if (dateString === todayTenantDateString()) return new Date().toISOString();
  return fromZonedTime(`${dateString}T23:59:59.999`, TENANT_TIME_ZONE).toISOString();
};

/**
 * 編集した在庫の変動日(出荷日など)を API に送る日時にする。暦日を変えていなければ元の日時のまま返す
 * (送り直すと在庫の変動日時も置き換わるため)
 */
export const editedDateToStockChangedAt = (dateString: string, original?: string | null): string =>
  original && toTenantDateString(original) === dateString ? original : tenantDateToStockChangedAt(dateString);

/**
 * 編集した入荷日を API に送る日時にする。暦日を変えていなければ元の日時のまま、変えたらテナントのTZでその日の始まり。
 * 入荷(在庫の増加)は早い時刻ほど、その日のうちに出庫したロットでも日付を変えられる。当日の始まりは未来にならない
 */
export const editedDateToDeliveredAt = (dateString: string, original?: string | null): string => {
  if (original && toTenantDateString(original) === dateString) return original;
  if (!isValid(parseISO(dateString))) return '';
  return fromZonedTime(`${dateString}T00:00:00`, TENANT_TIME_ZONE).toISOString();
};

/**
 * 日付文字列(YYYY-MM-DD)をUTC 00:00:00(or 入力時刻)形式に変換
 * APIへの送信時に使用（DateTimeFieldとの互換性のため）
 */
export const convertDateToUTC = (dateString?: string, hour?: number, min?: number, sec?: number): string => {
  if (!dateString) return '';

  const dateHour = hour ?? 0;
  const dateMin = min ?? 0;
  const dateSec = sec ?? 0;

  // YYYY-MM-DD形式の文字列をDateオブジェクトに変換
  const date = parseISO(dateString);

  if (!isValid(date)) {
    return '';
  }

  // UTC 00:00:00(or 入力時刻)として設定するため、日付のみを取得してUTCで再構築
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), dateHour, dateMin, dateSec, 0),
  );

  // formatISOを使用してISO形式で返す
  return formatISO(utcDate);
};

/**
 * 日付文字列(YYYY-MM-DD)とローカル時刻をISO形式に変換
 * ローカルタイムゾーンのオフセット付きISO形式（例: 2026-06-10T00:00:00+09:00）で返される
 * APIへの送信時に使用（DateTimeFieldとの互換性のため）
 */
export const convertDateTimeToLocalISO = (
  dateString?: string,
  hour?: number,
  min?: number,
  sec?: number,
): string => {
  if (!dateString) return '';

  const dateHour = hour ?? 0;
  const dateMin = min ?? 0;
  const dateSec = sec ?? 0;

  // YYYY-MM-DD形式の文字列をDateオブジェクトに変換
  const date = parseISO(dateString);

  if (!isValid(date)) {
    return '';
  }

  // ローカルタイムとして日時を構築（タイムゾーンを考慮してUTCに変換される）
  const localDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    dateHour,
    dateMin,
    dateSec,
    0,
  );

  // formatISOはローカルタイムをISO形式に変換
  return formatISO(localDate);
};

/**
 * 日付(YYYY-MM-DD)+時刻(HH:mm)をJST固定の絶対時刻(ISO)に変換する
 * 納品書メールの予約は自動送付(バックエンドがJSTで算出)と時刻基準を揃える必要があるため、
 * 端末TZに依存する convertDateTimeToLocalISO ではなくこちらを使う
 * 空値・不正値は空文字列を返す（既存 convertDateTimeToLocalISO と同じ規約）
 */
export const convertDateTimeToJstISO = (dateString?: string, time?: string): string => {
  if (!dateString || !time) return '';

  const jstDate = fromZonedTime(`${dateString}T${time}:00`, TENANT_TIME_ZONE);
  if (!isValid(jstDate)) return '';

  return jstDate.toISOString();
};

/** 締切日。99 を月末締切として扱う（締切期間算出時の月末センチネル）。 */
export const END_OF_MONTH = 99;

/**
 * 'YYYY-MM' と締切日(1〜31 または END_OF_MONTH=末日)から締切日(ISO文字列 yyyy-MM-dd)を返す。
 * 短い月で 29〜31 日締めが翌月へ繰り越さないよう当月末へ丸める（backend `_resolve_closing_date` と同じ
 * `min(closing_day, last_day)`。END_OF_MONTH=99 も同式で自然に月末へ丸まる）。
 */
export const toCutoff = (ym: string, day: number) => {
  const base = parseISO(`${ym}-01`);
  const lastDay = endOfMonth(base).getDate();
  return format(setDate(base, Math.min(day, lastDay)), 'yyyy-MM-dd');
};

/**
 * 締切日サイクルにおける対象期間（前回締切翌日〜今回締切日）を算出する。
 * 前回締切は「当月内で今回 cutoff より厳密に前の実 cutoff のうち最も遅い日」とする。
 * 短い月で複数の締め日が同日に丸まる場合（例: 31日締めと末日締めが2/28）に、同日 cutoff を前回締切に
 * 使って期間が逆転するのを防ぐ。当月内に前がなければ前月の最終締切の翌日から。
 */
export const computeGroupRange = (ym: string, day: number, sortedDays: number[]) => {
  const toDate = toCutoff(ym, day);
  const earlierCutoffs = sortedDays
    .map((d) => toCutoff(ym, d))
    .filter((cutoff) => cutoff < toDate)
    .sort();
  const sameMonthPrev = earlierCutoffs[earlierCutoffs.length - 1];
  if (sameMonthPrev) {
    return { fromDate: format(addDays(parseISO(sameMonthPrev), 1), 'yyyy-MM-dd'), toDate };
  }
  // サイクル先頭：前月の最終締切の翌日から
  const prevYm = format(subMonths(parseISO(`${ym}-01`), 1), 'yyyy-MM');
  const last = sortedDays[sortedDays.length - 1] ?? END_OF_MONTH;
  return { fromDate: format(addDays(parseISO(toCutoff(prevYm, last)), 1), 'yyyy-MM-dd'), toDate };
};
